import AnalyticsInstance from '@shared/utils/analytics';
import ApiRequestManagerInstance from '../ApiRequestManager';
import DeviceConfig from '@devices/config';
import FlavourConfig from '../../../shared/config/flavours';
import LoginErrors from '../../types/enums/loginError';
import Package from '../../../package.json';
import Pages from '../../types/enums/pages';
import Storage from '@storage';
import TranslationManager from '../../assets/locale/translationsManager';
import User from '../../classes/user';
import initAnonymous from '../../actions/login/initAnonymous';
import initPlans from '../../actions/plans/initPlans';
import { IDeviceRequest } from '../../types/interfaces/login';
import { ILoginError } from '../../types/interfaces/error';
import { clearChannels } from '@shared/store/slices/channels.slice';
import { pushRoute } from '../../store/thunks/route.thunk';
import { setDeviceInitialized, setSplash } from '../../store/slices/app.slice';
import { store } from '../../store/store';
import {
  clearEvents,
  setLastModifiedDate,
} from '@shared/store/slices/events.slice';
import {
  setUser,
  setInitialized,
  setCarrier,
  removeInitialized,
} from '../../store/slices/user.slice';

/**
 * Validates the user's login session by checking the stored token with the server.
 *
 * This function performs the following operations:
 * 1. Checks if a user token exists in storage
 * 2. If no token exists, initializes anonymous login
 * 3. If a token exists, validates it with the server via API call
 * 4. Handles various error scenarios (401, 403, 404) appropriately
 * 5. Updates the application state with user data, carrier info, and initializes analytics
 *
 * @param {boolean} [recheck=false] - If true, skips full initialization and only validates the token.
 *                                     Used for re-validation scenarios without resetting app state.
 * @returns {Promise<void>} A promise that resolves when the login check is complete.
 */
function checkLoginRequest(recheck = false) {
  store.dispatch(setDeviceInitialized(true));
  const promise = new Promise<void>(async (resolve) => {
    // Check if user token exists in storage
    // If no token is found, initialize anonymous login and exit early
    if (!Storage.get('userToken')) {
      initAnonymous();
      resolve();
      return;
    }
    try {
      // Get custom endpoint configuration from flavour config, or use defaults
      // This allows different flavours to customize the endpoints
      const { checkLogin } = FlavourConfig.customEndpoint || {};
      const { url, version, section } = checkLogin || {
        url: 'apiUrl',
        version: 'V4',
        section: 'auth',
      };

      // Make API request to validate the user token
      // Sends device information (UUID, model, platform, app version) to the server
      const response = (await ApiRequestManagerInstance.post({
        endpoint: {
          url,
          version,
          section,
          subsection: 'checkLogin',
          params: !checkLogin ? '/' + TranslationManager.currentLanguage : '',
        },
        data: {
          idDevice: DeviceConfig.info.uuid,
          model: DeviceConfig.info.model,
          platform: 'SMARTTV',
          version: Package.version,
        },
      })) as unknown as IDeviceRequest;

      // Execute device-specific login callback if configured
      // This allows platform-specific post-login operations
      DeviceConfig.loginCallback !== undefined
        ? await DeviceConfig.loginCallback()
        : null;

      // Only perform full initialization if this is not a recheck
      // Recheck mode is used for token validation without resetting app state
      if (!recheck) {
        // Remove initialized flag to ensure fresh start
        store.dispatch(removeInitialized());
        // Update carrier information in the store
        store.dispatch(setCarrier(response.data.carrier.id));
        // Reset last modified date to trigger fresh data fetch
        store.dispatch(setLastModifiedDate(undefined));

        // Initialize plans, then perform full app initialization
        // Plans initialization is done first as it may be required for other operations
        initPlans(() => {
          // Clear existing channels and events to ensure fresh data
          store.dispatch(clearChannels());
          store.dispatch(clearEvents());
          // Set user data from the API response
          store.dispatch(setUser(User.fromRequest(response)));
          // Mark the app as initialized
          store.dispatch(setInitialized());
          if (!AnalyticsInstance.isInitialized()) {
            AnalyticsInstance.initialize();
          }
          resolve();
        });
      } else {
        // For recheck mode, just resolve without full initialization
        resolve();
      }
    } catch (e: unknown) {
      // Handle login validation errors
      // Always initialize plans first, even on error, as it may be needed for error handling
      initPlans(() => {
        try {
          const { status, data } = (e as ILoginError).response;

          // Handle unauthorized (401) or not found (404) errors
          // These typically indicate invalid or expired token
          if (status === 401 || status === 404) {
            initAnonymous();
            resolve();
          } else if (status === 403) {
            // Handle forbidden (403) errors with specific error code handling
            let goToError = false;
            switch (data.error) {
              // These errors require showing an error page to the user
              case LoginErrors.MAX_DEVICES:
              case LoginErrors.USER_DISABLED:
              case LoginErrors.MAX_NUM_STBS:
              default:
                goToError = true;
                break;

              // These errors can be handled by falling back to anonymous login
              case LoginErrors.INACTIVE_USER:
              case LoginErrors.CARRIER_DISABLED:
              case LoginErrors.NO_TOKEN:
                initAnonymous();
                break;
            }

            // If error page is required, navigate to error page with message
            if (goToError) {
              store.dispatch(
                pushRoute({
                  route: { Page: Pages.ERROR, overwrite: true },
                  params: {
                    current: {
                      action: () => initAnonymous(),
                      // Display error message from server response
                      message: `${data?.message || ''} ${data?.error || ''}`,
                    },
                  },
                })
              );
              store.dispatch(setSplash({ display: false }));
            }
            resolve();
          }
        } catch (e) {
          // If error parsing fails or response structure is unexpected,
          // fall back to anonymous login as a safe default
          initAnonymous();
          resolve();
        }
      });
    }
  });

  return promise;
}

export default checkLoginRequest;
